import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsModal = ({ isOpen, onClose, onAccept, title = "Terms of Service & Provider Agreement" }) => {
  const [agreed, setAgreed] = useState(false);
  const [privacyEmail, setPrivacyEmail] = useState('');

  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/owner/company-details`).then(res => {
        setPrivacyEmail(res.data?.privacy_email || '');
      }).catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (agreed) {
      onAccept();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="terms-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
            data-testid="close-terms-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-600 mb-5 text-sm">
              By using GoodJob, you agree to be legally bound by these Terms. If you do not agree, you must not use the Platform. 
              GoodJob is governed by the laws of the Republic of the Philippines.
            </p>

            <p className="text-gray-600 mb-5 text-sm font-medium">
              By registering, you agree to our Privacy Policy and consent to the processing of your personal data in accordance with the Data Privacy Act of 2012.
            </p>

            <div className="space-y-4">
              {/* OFF-PLATFORM TRANSACTIONS - HIGHLIGHTED */}
              <div className="bg-red-50 p-4 rounded-xl border-2 border-red-300 relative" data-testid="off-platform-warning">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <h4 className="font-bold text-red-800 text-base">
                    Off-Platform Transactions — Strictly Prohibited
                  </h4>
                </div>
                <p className="text-red-800 text-sm mb-2">
                  All bookings, payments, and communications must happen exclusively through the GoodJob App. 
                  Users are strictly prohibited from:
                </p>
                <ul className="text-red-700 text-sm space-y-1 ml-4 mb-3 list-disc">
                  <li>Booking or accepting services outside the platform</li>
                  <li>Exchanging contact details to avoid platform fees</li>
                  <li>Accepting or making off-platform payments (cash, bank transfer, e-wallet, etc.)</li>
                </ul>
                <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                  <p className="text-red-900 text-sm font-bold">
                    Violation Penalty:
                  </p>
                  <ul className="text-red-800 text-sm space-y-0.5 ml-4 list-disc">
                    <li>Immediate account suspension</li>
                    <li>Permanent ban from the platform</li>
                    <li>Withholding / forfeiture of any pending funds</li>
                    <li>Possible legal action</li>
                  </ul>
                </div>
              </div>

              {/* Nature of Platform */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                  Platform Nature
                </h4>
                <p className="text-gray-600 text-sm">
                  GoodJob is a technology platform that connects independent service providers ("Providers") and customers ("Clients"). 
                  GoodJob is <strong>not</strong> an employer, staffing agency, or service provider. 
                  Providers are independent contractors solely responsible for their work quality, safety, and legal compliance.
                </p>
              </div>

              {/* Account & Verification */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                  Account & Identity Verification
                </h4>
                <p className="text-gray-600 text-sm">
                  GoodJob may require valid government ID, KYC verification, and background information. 
                  Accounts may be suspended pending verification. Providing false identity results in immediate termination. 
                  Failure to complete verification may disqualify users from using core features.
                </p>
              </div>

              {/* Escrow Payment & Fees */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">3</span>
                  Payments, Fees & Escrow
                </h4>
                <p className="text-gray-600 text-sm">
                  Payments are processed through GoodJob's escrow system: Client pays, funds are held in escrow, 
                  Provider completes the service, Client confirms, then payment is released. 
                  A platform service fee and/or provider commission is automatically deducted and disclosed before payment. 
                  If a dispute arises, funds are temporarily frozen until resolved.
                </p>
              </div>

              {/* Cancellations & Disputes */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">4</span>
                  Cancellations & Dispute Resolution
                </h4>
                <p className="text-gray-600 text-sm">
                  <strong>Client cancellation:</strong> Full refund before confirmation; partial or no refund after provider dispatch.  
                  <strong> Provider cancellation:</strong> May result in ranking drop or suspension.  
                  Disputes follow a 3-step process: internal resolution, platform mediation, and 
                  final binding decision by GoodJob. GoodJob may refund, pay partially, or penalize either party.
                </p>
              </div>

              {/* Prohibited Conduct */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">5</span>
                  Prohibited Conduct & Penalties
                </h4>
                <p className="text-gray-600 text-sm">
                  Strictly prohibited: fake bookings, no-show abuse, overcharging, harassment, theft, 
                  fraud, or unsafe behavior. Violations may result in immediate ban, forfeiture of funds, 
                  and legal reporting if necessary. Users must cooperate in investigations and submit evidence when required.
                </p>
              </div>

              {/* Liability & Indemnification */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">6</span>
                  Liability, Insurance & Indemnification
                </h4>
                <p className="text-gray-600 text-sm">
                  GoodJob shall <strong>not</strong> be liable for injury, death, damages, loss of property, service dissatisfaction, 
                  or provider misconduct. Providers are responsible for their own insurance — GoodJob does not provide coverage. 
                  Users agree to indemnify and hold GoodJob harmless from all claims, damages, and legal costs arising from their use of the platform.
                </p>
              </div>

              {/* Termination */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">7</span>
                  Account Termination
                </h4>
                <p className="text-gray-600 text-sm">
                  GoodJob may suspend or terminate any account for violations, at its discretion, 
                  and without prior notice if risk is present.
                </p>
              </div>

              {/* Privacy & Data Rights */}
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 mt-4">
                <h4 className="font-bold text-blue-800 text-base mb-2">Privacy & Data Rights</h4>
                <p className="text-gray-600 text-sm">
                  You may request access or deletion of your data
                  {privacyEmail ? (
                    <> by emailing <a href={`mailto:${privacyEmail}`} className="text-blue-600 font-semibold underline">{privacyEmail}</a></>
                  ) : (
                    <> through your Account Settings</>
                  )}
                  , or through the "Request My Data" and "Delete My Account" options in your Account Settings &gt; Privacy.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              data-testid="terms-checkbox"
            />
            <span className="text-gray-700 text-sm">
              I have read and agree to the <span className="font-semibold">Terms of Service</span> and{' '}
              <span className="font-semibold">Provider Agreement</span> of GoodJob. 
              I understand that off-platform transactions and other violations may result in 
              <span className="font-semibold text-red-600"> account suspension, permanent ban, forfeiture of funds, and possible legal action</span>.
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-11 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-100 font-medium transition"
              data-testid="decline-terms-btn"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={!agreed}
              className={`flex-1 h-11 rounded-full font-medium transition flex items-center justify-center gap-2 ${
                agreed 
                  ? 'bg-gray-900 text-white hover:bg-gray-800' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="accept-terms-btn"
            >
              <CheckCircle className="w-4 h-4" />
              I Agree
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
